import React from 'react';

import { Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Text } from '@opendoor/atoms/text';
import { Toasty } from '@opendoor/atoms/toast';
import { useAuth } from '@opendoor/auth-fe/context';
import { getCustomerNavigationHref } from 'components/account/impersonation';
import { useHubBuyerListingsAndTours } from 'components/hub/useHubBuyerListingsAndTours';
import { useHubPurchaseOffers } from 'components/hub/useHubPurchaseOffers';
import { useHubPurchasePage } from 'components/hub/useHubPurchasePage';
import { useHubSellerTransactions } from 'components/hub/useHubSellerTransactions';
import { useHubTourFeedbacks } from 'components/hub/useHubTourFeedbacks';
import type { HubBuyingTourFeedback } from 'components/hub/useHubTourFeedbacks';

import { ExploreNewHomesSection } from './ExploreNewHomesSection';
import {
  salePortfolioRows,
  TABLE_HOME_COUNT_THRESHOLD,
  usesHomeSalePortfolio,
} from './homePortfolioRows';
import { HomePortfolioTable } from './HomePortfolioTable';
import { HomePurchaseSection } from './HomePurchaseSection';
import { HomeSaleSection } from './HomeSaleSection';
import { HomeToursSection } from './HomeToursSection';
import { HubSectionHeader } from './HubSectionHeader';
import { orderHubSections } from './hubSectionOrdering';
import { isRecommendationsSectionVisible } from './hubSectionVisibility';
import { MortgageSection } from './MortgageSection';
import { PurchasePortfolioTable } from './PurchasePortfolioTable';
import { RecommendationsSection } from './RecommendationsSection';
import { SavedHomesSection } from './SavedHomesSection';
import { WelcomeHeader } from './WelcomeHeader';

const EMPTY_TOUR_FEEDBACKS: HubBuyingTourFeedback[] = [];

function HomeSectionStatus({
  title,
  isLoading,
  onRetry,
}: {
  title: string;
  isLoading: boolean;
  onRetry?: () => void;
}) {
  return (
    <Col className="w-full gap-4">
      <HubSectionHeader title={title} />
      <Col role={isLoading ? 'status' : 'alert'} className="items-start gap-4 py-8">
        <Text className="text-body-sm text-od-content-secondary">
          {isLoading ? 'Loading homes…' : 'We couldn’t load your homes.'}
        </Text>
        {!isLoading && onRetry && (
          <Button
            aria-label={`Retry ${title.toLowerCase()}`}
            analyticsName={title === 'Home purchase' ? 'home-purchase-retry' : 'home-sale-retry'}
            variant="secondary"
            size="md"
            onClick={onRetry}
          >
            Try again
          </Button>
        )}
      </Col>
    </Col>
  );
}

function HomeSectionEmptyState({ section }: { section: 'home-sale' | 'home-purchase' }) {
  const isSale = section === 'home-sale';
  const label = isSale ? 'Get a new home offer' : 'Browse homes';
  return (
    <Col className="w-full gap-4">
      <HubSectionHeader title={isSale ? 'Home sales' : 'Home purchases'} />
      <Row className="flex-wrap items-center justify-between gap-6 rounded-2xl border border-od-border-primary p-6">
        <Text className="min-w-0 flex-1 basis-60 text-body-sm text-od-content-secondary">
          {isSale
            ? 'No home sales yet. Get your client an Opendoor offer.'
            : 'No home purchases yet. Your clients can buy Opendoor homes with you.'}
        </Text>
        <Button
          href={getCustomerNavigationHref(isSale ? '/' : '/homes')}
          analyticsName={`${section}-empty-create`}
          aria-label={label}
          variant={isSale ? 'primary' : 'secondary'}
          size="md"
          className="w-full sm:w-auto"
        >
          {label}
        </Button>
      </Row>
    </Col>
  );
}

export const HubPage: React.FC = () => {
  const { user, authentication } = useAuth();
  const buyer = useHubBuyerListingsAndTours();
  const feedback = useHubTourFeedbacks();
  const seller = useHubSellerTransactions();
  const offers = useHubPurchaseOffers();
  const purchases = useHubPurchasePage({
    variables: { segment: 'ACTIVE', includeHomeSummary: true },
    enabled: true,
  });
  const summary = purchases.data?.page.homeSummary;
  const purchaseCards = summary?.rows.flatMap(({ offer }) => (offer ? [offer] : [])) ?? [];
  const salePortfolio = usesHomeSalePortfolio(seller.data ?? []);
  const noSales = seller.data?.length === 0;
  const purchasePortfolio = (summary?.homeCount ?? 0) >= TABLE_HOME_COUNT_THRESHOLD;
  const renderHomeSale = () => {
    if (seller.isPending) return <HomeSectionStatus title="Home sale" isLoading />;
    if (salePortfolio)
      return (
        <HomePortfolioTable
          section="home-sale"
          title="Home sales"
          rows={salePortfolioRows(seller.data ?? [])}
          action={{ label: 'Get a new home offer', href: '/' }}
          isLoading={false}
          hasError={seller.isError}
          onRetry={() => {
            void seller.refetch();
          }}
        />
      );
    if (seller.isError || seller.data === null)
      return (
        <HomeSectionStatus
          title="Home sale"
          isLoading={false}
          onRetry={() => {
            void seller.refetch();
          }}
        />
      );
    if (noSales) return <HomeSectionEmptyState section="home-sale" />;
    return <HomeSaleSection transactions={seller.data ?? []} isLoading={false} />;
  };
  const renderHomePurchase = () => {
    if (purchases.isPending) return <HomeSectionStatus title="Home purchase" isLoading />;
    if (!summary || (!purchasePortfolio && purchaseCards.length !== summary.homeCount))
      return (
        <HomeSectionStatus
          title="Home purchase"
          isLoading={false}
          onRetry={() => {
            void purchases.refetch();
          }}
        />
      );
    if (purchasePortfolio)
      return (
        <PurchasePortfolioTable
          key={
            authentication.state === 'authenticated'
              ? authentication.user.customerUuid
              : 'anonymous'
          }
        />
      );
    if (summary.homeCount === 0) return <HomeSectionEmptyState section="home-purchase" />;
    return <HomePurchaseSection offers={purchaseCards} isLoading={false} />;
  };
  const sections = orderHubSections([
    { id: 'home-sale', content: renderHomeSale() },
    { id: 'home-purchase', content: renderHomePurchase() },
    {
      id: 'mortgage',
      content: <MortgageSection offers={offers.data ?? []} />,
    },
    {
      id: 'home-tours',
      content: (
        <HomeToursSection
          tours={buyer.data?.tours ?? []}
          tourFeedbacks={feedback.data ?? EMPTY_TOUR_FEEDBACKS}
          isBuyerDataLoading={buyer.isPending}
          isTourFeedbackLoading={feedback.isPending}
        />
      ),
    },
    {
      id: 'saved-homes',
      content: (
        <SavedHomesSection properties={buyer.data?.favorites ?? []} isLoading={buyer.isPending} />
      ),
    },
    {
      id: 'explore-new-homes',
      content: <ExploreNewHomesSection />,
    },
    {
      id: 'recommendations',
      content: isRecommendationsSectionVisible(
        seller.data ?? [],
        seller.isPending,
        seller.isError || seller.data === null,
      ) ? (
        <RecommendationsSection />
      ) : null,
    },
  ]);

  return (
    <Toasty>
      <Col className="w-full items-center px-5 pt-2 pb-20 md:pt-4">
        {/* 812px max content width from the design. */}
        <Col className="w-full max-w-[812px] gap-10">
          <WelcomeHeader
            firstName={user?.firstName}
            fullName={user?.fullName}
            isNameLoading={authentication.state === 'authenticating'}
          />
          {sections.map(({ id, content }) => (
            <React.Fragment key={id}>{content}</React.Fragment>
          ))}
        </Col>
      </Col>
    </Toasty>
  );
};
